import React, { useEffect, useState } from 'react';
import './styles/LoginModal.css';
import { CloseSmall, User, Lock, Caution, Search, CloseOne, CheckOne } from '@icon-park/react';
import { login } from '../../../api/auth';
import { searchTenants } from '../../../api/tenant';

function LoginModal({ isOpen, onClose, onSwitchToRegister, onLoginSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // 租户选择相关状态
  const [tenantSearch, setTenantSearch] = useState('');
  const [searchResult, setSearchResult] = useState(null); // null: 未搜索, []: 无结果, [...]: 有结果
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [searching, setSearching] = useState(false);
  const [tenantError, setTenantError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setError('');
      setIsSubmitting(false);
      // 重置租户相关状态
      setTenantSearch('');
      setSearchResult(null);
      setSelectedTenant(null);
      setTenantError('');
    }
  }, [isOpen]);

  // 搜索租户
  const handleSearchTenant = async () => {
    const keyword = tenantSearch.trim();
    if (!keyword) {
      setTenantError('请输入租户名称或代码');
      return;
    }
    if (keyword.length < 2) {
      setTenantError('请输入至少2个字符');
      return;
    }

    setSearching(true);
    setTenantError('');

    try {
      const result = await searchTenants(keyword);
      const list = Array.isArray(result) ? result : [];
      setSearchResult(list);

      if (list.length === 1) {
        // 只有一个结果，自动选中
        setSelectedTenant(list[0]);
      } else if (list.length === 0) {
        setSelectedTenant(null);
        setTenantError('未找到匹配的租户');
      } else {
        setSelectedTenant(null);
      }
    } catch (err) {
      setSearchResult([]);
      setTenantError(err?.message || '搜索失败');
    } finally {
      setSearching(false);
    }
  };

  // 清除选中的租户
  const handleClearTenant = () => {
    setSelectedTenant(null);
    setSearchResult(null);
    setTenantSearch('');
    setTenantError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError('请输入账号和密码');
      return;
    }

    setError('');
    setIsSubmitting(true);
    try {
      // 传递 tenantId（如果已选择租户）
      const credentials = {
        username,
        password,
        tenantId: selectedTenant?.id || null,
      };
      const user = await login(credentials);
      if (onLoginSuccess) {
        onLoginSuccess(user);
      }
    } catch (loginError) {
      setError(loginError.message || '登录失败，请稍后再试');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content login-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <CloseSmall theme="outline" size={20} fill="#64748b" strokeWidth={3} />
        </button>
        
        <h2 className="modal-title">欢迎回来</h2>
        <p className="modal-subtitle">登录您的账户，继续使用数字标签服务</p>
        
        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="form-error"><Caution theme="filled" size={14} fill="#faad14" /> {error}</div>}

          {/* 租户选择区域 */}
          <div className="form-group">
            <label className="form-label">登录租户</label>
            <div className="tenant-search-wrapper">
              <input
                type="text"
                placeholder="输入租户名称或代码搜索"
                value={tenantSearch}
                onChange={(e) => {
                  setTenantSearch(e.target.value);
                  setTenantError('');
                  setSearchResult(null);
                  setSelectedTenant(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearchTenant();
                  }
                }}
                className={`tenant-search-input ${tenantError ? 'has-error' : ''}`}
              />
              <button
                type="button"
                onClick={handleSearchTenant}
                disabled={searching}
                className="tenant-search-btn"
              >
                <Search theme="outline" size={16} fill="currentColor" strokeWidth={3} />
                {searching ? '搜索中' : '搜索'}
              </button>
            </div>

            {/* 已选中的租户 */}
            {selectedTenant && (
              <div className="tenant-selected">
                <div className="tenant-selected-info">
                  <div className="tenant-avatar">
                    {(selectedTenant.tenantName || selectedTenant.name)?.charAt(0) || 'T'}
                  </div>
                  <div className="tenant-details">
                    <div className="tenant-name">{selectedTenant.tenantName || selectedTenant.name || '未命名租户'}</div>
                    <div className="tenant-code">{selectedTenant.tenantCode || selectedTenant.code || `ID: ${selectedTenant.id}`}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleClearTenant}
                  className="tenant-clear-btn"
                >
                  <CloseOne theme="outline" size={14} fill="currentColor" strokeWidth={3} />
                </button>
              </div>
            )}

            {/* 搜索结果列表（多个结果时显示） */}
            {!selectedTenant && searchResult && searchResult.length > 1 && (
              <div className="tenant-search-results">
                <div className="tenant-results-header">
                  找到 {searchResult.length} 个租户，请选择：
                </div>
                <div className="tenant-results-list">
                  {searchResult.map((tenant) => (
                    <button
                      key={tenant.id}
                      type="button"
                      onClick={() => setSelectedTenant(tenant)}
                      className="tenant-result-item"
                    >
                      <div className="tenant-avatar-sm">
                        {(tenant.tenantName || tenant.name)?.charAt(0) || 'T'}
                      </div>
                      <div className="tenant-details">
                        <div className="tenant-name">{tenant.tenantName || tenant.name || '未命名租户'}</div>
                        <div className="tenant-code">{tenant.tenantCode || tenant.code || `ID: ${tenant.id}`}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {tenantError && <p className="tenant-error">{tenantError}</p>}
            {!tenantError && !selectedTenant && (
              <p className="tenant-hint">请搜索并选择租户（平台管理员可直接登录）</p>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">账户名称</label>
            <div className="input-wrapper">
              <span className="input-icon">
                <User theme="outline" size={20} fill="currentColor" strokeWidth={3} />
              </span>
              <input
                type="text"
                placeholder="请输入您的账户名"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="form-input"
                autoComplete="username"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label">登录密码</label>
            <div className="input-wrapper">
              <span className="input-icon">
                <Lock theme="outline" size={20} fill="currentColor" strokeWidth={3} />
              </span>
              <input
                type="password"
                placeholder="请输入您的密码"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                autoComplete="current-password"
              />
            </div>
          </div>
          
          <div className="form-actions">
            <a href="#" className="forgot-password">忘记密码？</a>
          </div>
          
          <button type="submit" className="btn-login" disabled={isSubmitting}>
            {isSubmitting ? '登录中...' : '登录'}
          </button>
          
          <div className="switch-form">
            还没有账号？<a href="#" onClick={onSwitchToRegister}>立即注册</a>
          </div>
        </form>
      </div>
    </div>
  );
}

export default LoginModal;
